import { useMemo, useState } from "react";

import { useCanEdit } from "@/api/session";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Icon,
  InlineAlert,
  Kbd,
  PageHeader,
  RelativeTime,
  Select,
  Skeleton,
  TextField,
  Toolbar,
  type DataTableColumn,
  type SelectOption,
} from "@/components";
import { formatInteger } from "@/lib/format";
import { useDebouncedValue, useHotkey, useSearchParamsInPlace } from "@/lib/hooks";
import { useReviewItem, useReviewItems, useReviewReasons, type ReviewItem, type ReviewStatus } from "./api";
import { DecisionPanel } from "./DecisionPanel";
import styles from "./ReviewPage.module.css";

const PAGE_SIZE = 50;

const STATUS_OPTIONS: ReadonlyArray<SelectOption<ReviewStatus>> = [
  { value: "open", label: "Open" },
  { value: "deferred", label: "Deferred" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
];

function isStatus(value: string | null): value is ReviewStatus {
  return value === "open" || value === "deferred" || value === "accepted" || value === "rejected";
}

/** Data the importers couldn't settle: evidence side by side, then accept, reject or defer (recorded). */
export function ReviewPage() {
  const [searchParams, setSearchParams] = useSearchParamsInPlace();
  const canEdit = useCanEdit();
  const statusParam = searchParams.get("status");
  const status: ReviewStatus = isStatus(statusParam) ? statusParam : "open";
  const reason = searchParams.get("reason") ?? undefined;
  const selectedId = searchParams.get("item");
  const [searchText, setSearchText] = useState(searchParams.get("q") ?? "");
  const query = useDebouncedValue(searchText.trim(), 250);
  const [page, setPage] = useState(0);
  const items = useReviewItems({
    status,
    reason_code: reason,
    q: query || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  const reasons = useReviewReasons(status);
  const rows = useMemo(() => items.data?.items ?? [], [items.data]);
  const activeId = selectedId ?? rows[0]?.id ?? null;
  const detail = useReviewItem(activeId);

  function setParam(changes: Record<string, string | null>) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (!value) next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }

  function select(id: string | null) {
    setParam({ item: id });
  }

  const index = rows.findIndex((row) => row.id === activeId);
  function move(step: number) {
    const next = rows[Math.min(rows.length - 1, Math.max(0, (index === -1 ? -1 : index) + step))];
    if (next) select(next.id);
  }
  useHotkey("j", () => move(1));
  useHotkey("k", () => move(-1));

  const nextAfterDecision = index >= 0 ? (rows[index + 1] ?? rows[index - 1] ?? null) : null;

  const reasonOptions = useMemo<SelectOption[]>(
    () => [
      { value: "all", label: "Every reason" },
      ...(reasons.data ?? []).map((row) => ({
        value: row.reason_code,
        label: row.label,
        detail: `${row.count} items`,
      })),
    ],
    [reasons.data],
  );

  const columns: DataTableColumn<ReviewItem>[] = [
    {
      id: "item",
      header: "Item",
      width: "wide",
      cell: (row) => (
        <button
          type="button"
          className={styles.rowButton}
          aria-current={row.id === activeId}
          onClick={() => select(row.id)}
        >
          <span className={styles.rowTitle}>{row.entity_label}</span>
          <span className={styles.rowSummary}>{row.summary}</span>
        </button>
      ),
    },
    {
      id: "reason",
      header: "Reason",
      width: "fit",
      cell: (row) => (
        <Badge tone={row.priority >= 40 ? "danger" : row.priority >= 30 ? "warning" : "neutral"}>
          {row.reason_label}
        </Badge>
      ),
    },
    { id: "added", header: "Added", width: "fit", cell: (row) => <RelativeTime value={row.created_at} /> },
  ];

  const total = items.data?.count ?? 0;
  const openTotal = status === "open" ? total : null;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Review"
        description="Names, numbers and matches the importers couldn’t settle on their own. Every decision is recorded with your note."
        meta={
          openTotal !== null && items.data ? (
            <Badge tone={openTotal ? "warning" : "success"}>{formatInteger(openTotal)} open</Badge>
          ) : null
        }
      />
      <Toolbar
        label="Filter the review queue"
        end={
          <span className={styles.hint}>
            <Kbd keys={["j"]} /> <Kbd keys={["k"]} /> next and previous
          </span>
        }
      >
        <TextField
          label="Search"
          hideLabel
          size="sm"
          type="search"
          className={styles.search}
          placeholder="Material, substance or listing"
          prefix={<Icon name="search" />}
          value={searchText}
          onValueChange={(value) => {
            setSearchText(value);
            setPage(0);
          }}
        />
        <Select
          label="Status"
          size="sm"
          options={STATUS_OPTIONS}
          value={status}
          onValueChange={(value) => {
            setPage(0);
            setParam({ status: value === "open" ? null : value, item: null });
          }}
        />
        <Select
          label="Reason"
          size="sm"
          options={reasonOptions}
          value={reason ?? "all"}
          onValueChange={(value) => {
            setPage(0);
            setParam({ reason: value === "all" ? null : value, item: null });
          }}
        />
      </Toolbar>

      <div className={styles.split}>
        <div className={styles.queue}>
          <DataTable
            caption="Review items"
            columns={columns}
            rows={items.data?.items}
            getRowId={(row) => row.id}
            selectedRowId={activeId}
            loading={items.isPending}
            error={items.error}
            onRetry={() => void items.refetch()}
            empty={
              status === "open" && !reason && !query ? (
                <EmptyState
                  variant="inline"
                  headingLevel={3}
                  icon="checkCircle"
                  title="Nothing to review"
                  description="New items appear when imports, crawls or identity resolution find something uncertain."
                />
              ) : (
                <EmptyState
                  variant="inline"
                  headingLevel={3}
                  title="No items match"
                  actions={
                    <Button
                      onClick={() => {
                        setSearchText("");
                        setSearchParams({}, { replace: true });
                      }}
                    >
                      Show open items
                    </Button>
                  }
                />
              )
            }
          />
          {total > PAGE_SIZE ? (
            <nav className={styles.pager} aria-label="Pages">
              <span className={styles.pagerText}>
                {formatInteger(page * PAGE_SIZE + 1)}–{formatInteger(Math.min(total, (page + 1) * PAGE_SIZE))} of{" "}
                {formatInteger(total)}
              </span>
              <Button size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button size="sm" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </nav>
          ) : null}
        </div>
        <div className={styles.panelColumn}>
          {!activeId ? (
            <EmptyState
              variant="inline"
              icon="clipboardCheck"
              title="Choose an item"
              description="Its evidence and the decision appear here."
            />
          ) : detail.isPending ? (
            <Skeleton lines={8} />
          ) : detail.isError ? (
            <InlineAlert
              tone="danger"
              title="Couldn’t load this item"
              action={<Button onClick={() => void detail.refetch()}>Retry</Button>}
            >
              {detail.error.message}
            </InlineAlert>
          ) : (
            <DecisionPanel
              key={detail.data.id}
              item={detail.data}
              canEdit={canEdit}
              onDecided={() => select(nextAfterDecision?.id ?? null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
